import React, { useState } from "react";
import useFinancialTransactions from "../../hooks/useFinancialTransactions";
import { TRANSACTION_TYPES } from "../../constants/transactionTypes";
import TransactionForm from "../Revenue/TransactionForm";
import TransactionTable from "../Revenue/TransactionTable";
import styles from "./ExpensesTab.module.css";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";

const MONTHS = [
  "يناير",
  "فبراير",
  "مارس",
  "أبريل",
  "مايو",
  "يونيو",
  "يوليو",
  "أغسطس",
  "سبتمبر",
  "أكتوبر",
  "نوفمبر",
  "ديسمبر",
];

const ExpensesTab = () => {
  const {
    transactions,
    loading,
    error,
    filters,
    setFilters,
    createTransaction,
    updateTransaction,
    deleteTransaction,
  } = useFinancialTransactions(
    TRANSACTION_TYPES.EXPENSE
  );

  // =========================
  // Form
  // =========================

  const [editingItem, setEditingItem] =
    useState(null);

  const [showForm, setShowForm] =
    useState(false);

  // =========================
  // Delete
  // =========================

  const [itemToDelete, setItemToDelete] =
    useState(null);

  const [deleting, setDeleting] =
    useState(false);

  // =========================
  // Date Filter
  // =========================

  const [showDateFilter, setShowDateFilter] =
    useState(false);

  /*
   * day | month | year
   */
  const [filterType, setFilterType] =
    useState("day");

  /*
   * From | To
   */
  const [selectingField, setSelectingField] =
    useState("From");

  /*
   * التاريخ الحقيقي بعد الضغط على تأكيد
   */
  const [fromDate, setFromDate] =
    useState(null);

  const [toDate, setToDate] =
    useState(null);

  /*
   * التاريخ المؤقت داخل الـ Modal
   *
   * لا يتم اعتماده إلا بعد الضغط على "تأكيد"
   */
  const [tempDate, setTempDate] =
    useState(null);

  /*
   * بيانات التاريخ الموجودة داخل الـ Modal
   */
  const [selectedYear, setSelectedYear] =
    useState(new Date().getFullYear());

  const [selectedMonth, setSelectedMonth] =
    useState(new Date().getMonth());

  const [selectedDay, setSelectedDay] =
    useState(new Date().getDate());

  // =========================
  // Helpers
  // =========================

  const pad = (value) =>
    String(value).padStart(2, "0");

  const formatDisplayDate = (date) => {
    if (!date) return "";

    return `${pad(date.getDate())}/${pad(
      date.getMonth() + 1
    )}/${date.getFullYear()}`;
  };

  const formatApiStart = (date) => {
    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T00:00:00`;
  };

  const formatApiEnd = (date) => {
    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T23:59:59`;
  };

  // =========================
  // Form Submit
  // =========================

  const handleSubmit = async (data) => {
    if (editingItem) {
      await updateTransaction(
        editingItem.id,
        data
      );
    } else {
      await createTransaction(data);
    }

    setShowForm(false);
    setEditingItem(null);
  };

  // =========================
  // Edit
  // =========================

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  // =========================
  // Delete
  // =========================

  const handleDelete = (item) => {
    setItemToDelete(item);
  };

  const confirmDelete = async () => {
    if (!itemToDelete?.id) return;

    try {
      setDeleting(true);

      await deleteTransaction(
        itemToDelete.id
      );

      setItemToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    if (deleting) return;

    setItemToDelete(null);
  };

  // =========================
  // Open Date Modal
  // =========================

  const openDateFilter = (field) => {
    setSelectingField(field);

    /*
     * لو فيه تاريخ محفوظ بالفعل،
     * نبدأ منه.
     *
     * لو مفيش، نبدأ من اليوم.
     */
    const currentDate =
      field === "From"
        ? fromDate
        : toDate;

    const dateToUse =
      currentDate || new Date();

    /*
     * مهم:
     * الـ tempDate منفصل عن fromDate / toDate
     */
    setTempDate(dateToUse);

    setSelectedYear(
      dateToUse.getFullYear()
    );

    setSelectedMonth(
      dateToUse.getMonth()
    );

    setSelectedDay(
      dateToUse.getDate()
    );

    /*
     * افتح الـ Modal
     */
    setShowDateFilter(true);
  };

  // =========================
  // Temporary Date
  // =========================

  const selectTempDate = (date) => {
    if (!date) return;

    /*
     * لا نغير From أو To هنا.
     * فقط التحديد المؤقت.
     */
    setTempDate(date);

    setSelectedYear(
      date.getFullYear()
    );

    setSelectedMonth(
      date.getMonth()
    );

    setSelectedDay(
      date.getDate()
    );
  };

  // =========================
  // Select Day
  // =========================

  const handleSelectDay = (day) => {
    const date = new Date(
      selectedYear,
      selectedMonth,
      day
    );

    selectTempDate(date);
  };

  // =========================
  // Select Month
  // =========================

  const handleSelectMonth = (monthIndex) => {
    let date;

    /*
     * From الشهر = أول يوم
     */
    if (selectingField === "From") {
      date = new Date(
        selectedYear,
        monthIndex,
        1
      );
    } else {
      /*
       * To الشهر = آخر يوم
       */
      date = new Date(
        selectedYear,
        monthIndex + 1,
        0
      );
    }

    selectTempDate(date);
  };

  // =========================
  // Select Year
  // =========================

  const handleSelectYear = (year) => {
    let date;

    /*
     * From السنة = 1 يناير
     */
    if (selectingField === "From") {
      date = new Date(year, 0, 1);
    } else {
      /*
       * To السنة = 31 ديسمبر
       */
      date = new Date(year, 11, 31);
    }

    selectTempDate(date);
  };

  // =========================
  // Confirm Modal
  // =========================

  const confirmDateSelection = () => {
    if (!tempDate) return;

    /*
     * هنا فقط نعتمد الاختيار
     */
    if (selectingField === "From") {
      setFromDate(tempDate);
    } else {
      setToDate(tempDate);
    }

    /*
     * نقفل الـ Modal بعد التأكيد
     */
    setShowDateFilter(false);
  };

  // =========================
  // Cancel Modal
  // =========================

  const cancelDateSelection = () => {
    /*
     * لا نغير fromDate / toDate
     */
    setShowDateFilter(false);
  };

  // =========================
  // Apply API Filter
  // =========================

  const applyDateFilter = () => {
    if (!fromDate || !toDate) {
      alert(
        "من فضلك اختر التاريخ من وإلى"
      );

      return;
    }

    if (fromDate > toDate) {
      alert(
        "تاريخ البداية يجب أن يكون قبل تاريخ النهاية"
      );

      return;
    }

    /*
     * هنا فقط يتم تعديل filters
     *
     * وبالتالي هنا فقط الـ hook
     * هيعمل Request جديد
     */
    setFilters((prev) => ({
      ...prev,

      From: formatApiStart(fromDate),

      To: formatApiEnd(toDate),

      PageIndex: 1,
    }));
  };

  // =========================
  // Clear Filter
  // =========================

  const clearDateFilter = () => {
    setFromDate(null);
    setToDate(null);
    setTempDate(null);

    setFilters((prev) => ({
      ...prev,

      From: "",
      To: "",

      PageIndex: 1,
    }));
  };

  // =========================
  // Calendar
  // =========================

  const daysInMonth = new Date(
    selectedYear,
    selectedMonth + 1,
    0
  ).getDate();

  const currentYear =
    new Date().getFullYear();

  /*
   * آخر 5 سنوات
   */
  const years = Array.from(
    { length: 5 },
    (_, index) =>
      currentYear - index
  );

  return (
    <div
      className={styles.container}
      dir="rtl"
    >
      {/* =====================================================
          HEADER
      ====================================================== */}

      <div className={styles.header}>
        <div>
          <h3>المصروفات</h3>

          <p className={styles.subtitle}>
            إدارة ومتابعة المصروفات
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingItem(null);
            setShowForm(true);
          }}
        >
          + إضافة مصروف
        </button>
      </div>

      {/* =====================================================
          DATE FILTER
      ====================================================== */}

      <div className={styles.filterBox}>

        {/* FROM */}

        <div className={styles.dateField}>
          <label>من</label>

          <button
            type="button"
            className={styles.dateButton}
            onClick={() =>
              openDateFilter("From")
            }
          >
            {fromDate
              ? formatDisplayDate(fromDate)
              : "اختر تاريخ البداية"}
          </button>
        </div>

        {/* TO */}

        <div className={styles.dateField}>
          <label>إلى</label>

          <button
            type="button"
            className={styles.dateButton}
            onClick={() =>
              openDateFilter("To")
            }
          >
            {toDate
              ? formatDisplayDate(toDate)
              : "اختر تاريخ النهاية"}
          </button>
        </div>

        {/* ACTIONS */}

        <div className={styles.filterActions}>

          <button
            type="button"
            className={styles.applyButton}
            onClick={applyDateFilter}
          >
            تطبيق
          </button>

          <button
            type="button"
            className={styles.clearButton}
            onClick={clearDateFilter}
          >
            مسح
          </button>

        </div>
      </div>

      {/* =====================================================
          SELECTED RANGE
      ====================================================== */}

      <div className={styles.selectedRange}>

        <span>الفترة المختارة:</span>

        <strong>
          {fromDate
            ? formatDisplayDate(fromDate)
            : "غير محدد"}

          <span className={styles.arrow}>
            {" → "}
          </span>

          {toDate
            ? formatDisplayDate(toDate)
            : "غير محدد"}
        </strong>

      </div>

      {/* =====================================================
          ERROR
      ====================================================== */}

      {error && (
        <p className={styles.error}>
          {error}
        </p>
      )}

      {/* =====================================================
          FORM
      ====================================================== */}

      {showForm && (
        <TransactionForm
          initialData={editingItem}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
          submitLabel={
            editingItem
              ? "تحديث"
              : "إضافة"
          }
        />
      )}

      {/* =====================================================
          TABLE
      ====================================================== */}

      <TransactionTable
        transactions={transactions}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* =====================================================
          DELETE DIALOG
      ====================================================== */}

      <ConfirmDialog
        isOpen={!!itemToDelete}
        title="تأكيد الحذف"
        message={`هل أنت متأكد من حذف "${
          itemToDelete?.title ||
          itemToDelete?.name ||
          "هذا العنصر"
        }"؟ لا يمكن التراجع عن هذا الإجراء.`}
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* =====================================================
          DATE MODAL
      ====================================================== */}

      {showDateFilter && (
        <div
          className={styles.modalOverlay}
          onClick={cancelDateSelection}
        >
          <div
            className={styles.modal}
            onClick={(event) =>
              event.stopPropagation()
            }
          >

            {/* =========================
                MODAL HEADER
            ========================== */}

            <div
              className={
                styles.modalHeader
              }
            >
              <div>
                <h3>
                  اختيار{" "}
                  {selectingField === "From"
                    ? "تاريخ البداية"
                    : "تاريخ النهاية"}
                </h3>

                <p>
                  اختر اليوم أو الشهر أو السنة
                  ثم اضغط تأكيد
                </p>
              </div>

              <button
                type="button"
                className={
                  styles.closeButton
                }
                onClick={
                  cancelDateSelection
                }
              >
                ×
              </button>
            </div>

            {/* =========================
                TYPE TABS
            ========================== */}

            <div
              className={styles.typeTabs}
            >

              <button
                type="button"
                className={
                  filterType === "day"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType("day")
                }
              >
                يوم
              </button>

              <button
                type="button"
                className={
                  filterType === "month"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType("month")
                }
              >
                شهر
              </button>

              <button
                type="button"
                className={
                  filterType === "year"
                    ? styles.activeTab
                    : ""
                }
                onClick={() =>
                  setFilterType("year")
                }
              >
                سنة
              </button>

            </div>

            {/* =================================================
                DAY
            ================================================== */}

            {filterType === "day" && (
              <>
                <div
                  className={
                    styles.calendarHeader
                  }
                >

                  <button
                    type="button"
                    onClick={() => {
                      const previous =
                        new Date(
                          selectedYear,
                          selectedMonth - 1,
                          1
                        );

                      setSelectedYear(
                        previous.getFullYear()
                      );

                      setSelectedMonth(
                        previous.getMonth()
                      );
                    }}
                  >
                    ‹
                  </button>

                  <strong>
                    {MONTHS[selectedMonth]}{" "}
                    {selectedYear}
                  </strong>

                  <button
                    type="button"
                    onClick={() => {
                      const next =
                        new Date(
                          selectedYear,
                          selectedMonth + 1,
                          1
                        );

                      setSelectedYear(
                        next.getFullYear()
                      );

                      setSelectedMonth(
                        next.getMonth()
                      );
                    }}
                  >
                    ›
                  </button>

                </div>

                <div
                  className={styles.dayGrid}
                >

                  {Array.from(
                    {
                      length: daysInMonth,
                    },
                    (_, index) => {
                      const day =
                        index + 1;

                      /*
                       * مقارنة مع التاريخ المؤقت
                       */
                      const isSelected =
                        tempDate &&
                        tempDate.getFullYear() ===
                          selectedYear &&
                        tempDate.getMonth() ===
                          selectedMonth &&
                        tempDate.getDate() ===
                          day;

                      return (
                        <button
                          key={day}
                          type="button"
                          className={
                            isSelected
                              ? `${styles.dayButton} ${styles.selected}`
                              : styles.dayButton
                          }
                          onClick={() =>
                            handleSelectDay(
                              day
                            )
                          }
                        >
                          {day}
                        </button>
                      );
                    }
                  )}

                </div>
              </>
            )}

            {/* =================================================
                MONTH
            ================================================== */}

            {filterType === "month" && (
              <>
                <div
                  className={
                    styles.calendarHeader
                  }
                >

                  <button
                    type="button"
                    onClick={() =>
                      setSelectedYear(
                        (prev) => prev - 1
                      )
                    }
                  >
                    ‹
                  </button>

                  <strong>
                    {selectedYear}
                  </strong>

                  <button
                    type="button"
                    onClick={() =>
                      setSelectedYear(
                        (prev) => prev + 1
                      )
                    }
                  >
                    ›
                  </button>

                </div>

                <div
                  className={
                    styles.monthGrid
                  }
                >

                  {MONTHS.map(
                    (month, index) => {

                      const isSelected =
                        tempDate &&
                        tempDate.getFullYear() ===
                          selectedYear &&
                        tempDate.getMonth() ===
                          index;

                      return (
                        <button
                          key={month}
                          type="button"
                          className={
                            isSelected
                              ? `${styles.monthButton} ${styles.selected}`
                              : styles.monthButton
                          }
                          onClick={() =>
                            handleSelectMonth(
                              index
                            )
                          }
                        >
                          {month}
                        </button>
                      );
                    }
                  )}

                </div>
              </>
            )}

            {/* =================================================
                YEAR
            ================================================== */}

            {filterType === "year" && (
              <div
                className={styles.yearGrid}
              >

                {years.map((year) => {

                  const isSelected =
                    tempDate &&
                    tempDate.getFullYear() ===
                      year;

                  return (
                    <button
                      key={year}
                      type="button"
                      className={
                        isSelected
                          ? `${styles.yearButton} ${styles.selected}`
                          : styles.yearButton
                      }
                      onClick={() =>
                        handleSelectYear(
                          year
                        )
                      }
                    >
                      {year}
                    </button>
                  );
                })}

              </div>
            )}

            {/* =================================================
                TEMPORARY SELECTION
            ================================================== */}

            <div
              className={
                styles.modalFooter
              }
            >

              <div
                className={
                  styles.tempSelectedDate
                }
              >
                <span>
                  الاختيار الحالي:
                </span>

                <strong>
                  {tempDate
                    ? formatDisplayDate(
                        tempDate
                      )
                    : "لم يتم الاختيار"}
                </strong>
              </div>

              <div
                className={
                  styles.modalActions
                }
              >

                <button
                  type="button"
                  className={
                    styles.cancelModalButton
                  }
                  onClick={
                    cancelDateSelection
                  }
                >
                  إلغاء
                </button>

                <button
                  type="button"
                  className={
                    styles.confirmModalButton
                  }
                  disabled={!tempDate}
                  onClick={
                    confirmDateSelection
                  }
                >
                  تأكيد
                </button>

              </div>

            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesTab;


